/**
 * ResourceImportModal — Paste-from-Excel import for resource items.
 *
 * Users copy rows from the L4E Resource Import Template (or any tab-delimited
 * source) and paste into a textarea. The component parses, validates, previews,
 * and on confirmation returns fully-formed PreflightItem[] to the parent.
 *
 * Expected column order (matches the Excel template):
 *   Resource Name | Category | Phase | Quantity | Unit | Unit Cost | Priority | Description
 */

import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { PreflightItem, ResourceCategory, ResourcePhase, PreflightPriority } from '../../Data/PreFlight/preflightTypes';
import { RESOURCE_CATEGORIES, RESOURCE_PHASES, normalizeCategory } from '../../Data/PreFlight/preflightTypes';
import styles from './ResourceImportModal.module.css';

/* ── Constants ────────────────────────────────────────────────────── */

const VALID_CATEGORIES = new Set<string>(RESOURCE_CATEGORIES);
const VALID_PHASES = new Set<string>(RESOURCE_PHASES);
const VALID_PRIORITIES = new Set<string>(['critical', 'important', 'nice_to_have']);

const PHASE_ALIASES: Record<string, ResourcePhase> = {
  preparation: 'preparation',
  prep: 'preparation',
  origination: 'preparation',
  'partner alignment': 'preparation',
  mobilization: 'preparation',
  recruitment: 'preparation',
  operational: 'operational',
  execution: 'operational',
  sustainment: 'sustainment',
  closeout: 'sustainment',
  maintenance: 'sustainment',
};

const PRIORITY_ALIASES: Record<string, PreflightPriority> = {
  critical: 'critical',
  high: 'critical',
  important: 'important',
  medium: 'important',
  nice_to_have: 'nice_to_have',
  'nice to have': 'nice_to_have',
  low: 'nice_to_have',
  optional: 'nice_to_have',
};

/* ── Parsed row type ─────────────────────────────────────────────── */

interface ParsedRow {
  name: string;
  category: ResourceCategory;
  phase: ResourcePhase;
  quantity: number;
  unit: string;
  unitCost: number;
  priority: PreflightPriority;
  description: string;
  warnings: string[];
}

/* ── UUID helper ─────────────────────────────────────────────────── */

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto?.randomUUID) return crypto.randomUUID().toUpperCase();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/* ── Parser ───────────────────────────────────────────────────────── */

function parseClipboardData(raw: string): { rows: ParsedRow[]; errors: string[] } {
  const lines = raw.split('\n').map((l) => l.replace(/\r$/, ''));
  const rows: ParsedRow[] = [];
  const errors: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const cols = line.split('\t');
    const name = (cols[0] ?? '').trim();

    // Skip header rows, hint rows, and subtotal/total rows
    if (!name) continue;
    if (/^resource\s*name$/i.test(name)) continue;
    if (/^required|^REQUIRED/i.test(name)) continue;
    if (/subtotal|grand total|contingency|total with/i.test(name)) continue;
    if (/^#$/i.test(name)) continue;

    const warnings: string[] = [];

    // Category
    const rawCat = (cols[1] ?? '').trim().toLowerCase().replace(/\s+/g, '_');
    let category: ResourceCategory = 'materials';
    if (rawCat && VALID_CATEGORIES.has(rawCat)) {
      category = rawCat as ResourceCategory;
    } else if (rawCat) {
      category = normalizeCategory(rawCat);
      if (category === 'other' && rawCat !== 'other') {
        warnings.push(`Category "${cols[1]?.trim()}" → other`);
      }
    }

    // Phase
    const rawPhase = (cols[2] ?? '').trim().toLowerCase();
    let phase: ResourcePhase = 'preparation';
    if (rawPhase && PHASE_ALIASES[rawPhase]) {
      phase = PHASE_ALIASES[rawPhase];
    } else if (rawPhase && VALID_PHASES.has(rawPhase)) {
      phase = rawPhase as ResourcePhase;
    } else if (rawPhase) {
      warnings.push(`Phase "${cols[2]?.trim()}" → preparation`);
    }

    // Quantity
    const rawQty = (cols[3] ?? '').trim();
    const quantity = rawQty ? (parseFloat(rawQty) || 1) : 1;

    // Unit
    const unit = (cols[4] ?? '').trim() || 'each';

    // Unit Cost
    const rawCost = (cols[5] ?? '').trim().replace(/[$,]/g, '');
    const unitCost = rawCost ? (parseFloat(rawCost) || 0) : 0;

    // Priority
    const rawPri = (cols[6] ?? '').trim().toLowerCase().replace(/\s+/g, '_');
    let priority: PreflightPriority = 'important';
    if (rawPri && PRIORITY_ALIASES[rawPri]) {
      priority = PRIORITY_ALIASES[rawPri];
    } else if (rawPri && VALID_PRIORITIES.has(rawPri)) {
      priority = rawPri as PreflightPriority;
    }

    // Description
    const description = (cols[7] ?? '').trim();

    rows.push({ name, category, phase, quantity, unit, unitCost, priority, description, warnings });
  }

  if (rows.length === 0) {
    errors.push('No valid data rows found. Make sure you copied the data rows (not headers).');
  }

  return { rows, errors };
}

/* ── Convert parsed rows to PreflightItem[] ──────────────────────── */

function rowsToItems(
  rows: ParsedRow[],
  projectGuid: string,
  startSortOrder: number
): PreflightItem[] {
  const now = new Date().toISOString();
  return rows.map((row, idx) => ({
    item_guid: generateUUID(),
    project_guid: projectGuid,
    phase: row.phase,
    category: row.category as any,
    label: row.name,
    description: row.description || '',
    requirement_type: 'threshold' as const,
    target_quantity: row.quantity,
    unit: row.unit || null,
    current_quantity: 0,
    is_fulfilled: false,
    state: 'seeking' as const,
    priority: row.priority,
    descriptor_tags: [],
    matching_conditions: null,
    linked_offer_guid: null,
    withdrawn_at: null,
    visibility: 'internal' as const,
    mission_subkpi_key: null,
    tags: [],
    sort_order: startSortOrder + idx,
    created_by: '',
    created_at: now,
    updated_at: now,
    cost_category: row.category as any,
    unit_cost: row.unitCost || null,
    actual_cost: null,
    cost_currency: null,
    cost_note: null,
    gantt_task_id: null,
    percent_complete: 0,
  }));
}

/* ── Component ────────────────────────────────────────────────────── */

interface ResourceImportModalProps {
  projectGuid: string;
  existingCount: number;
  onImport: (items: PreflightItem[]) => void;
  onCancel: () => void;
}

export default function ResourceImportModal({
  projectGuid,
  existingCount,
  onImport,
  onCancel,
}: ResourceImportModalProps) {
  const { t } = useAppTranslation('kpi');
  const [rawText, setRawText] = useState('');
  const [parsed, setParsed] = useState<{ rows: ParsedRow[]; errors: string[] } | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Auto-focus textarea
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Parse on text change
  useEffect(() => {
    if (rawText.trim()) {
      setParsed(parseClipboardData(rawText));
    } else {
      setParsed(null);
    }
  }, [rawText]);

  // Handle paste event
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData('text/plain');
    if (text) {
      e.preventDefault();
      setRawText(text);
    }
  }, []);

  // Backdrop click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onCancel();
      }
    },
    [onCancel]
  );

  // Confirm import
  const handleConfirm = useCallback(() => {
    if (!parsed || parsed.rows.length === 0) return;
    const items = rowsToItems(parsed.rows, projectGuid, existingCount);
    onImport(items);
  }, [parsed, projectGuid, existingCount, onImport]);

  const totalEstimated = useMemo(() => {
    if (!parsed) return 0;
    return parsed.rows.reduce((sum, r) => sum + r.quantity * r.unitCost, 0);
  }, [parsed]);

  const hasWarnings = parsed?.rows.some((r) => r.warnings.length > 0) ?? false;

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={handleBackdropClick} role="dialog" aria-modal="true">
      <div className={styles.panel} ref={panelRef}>
        {/* Header */}
        <div className={styles.header}>
          <h3 className={styles.title}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <line x1="9" y1="15" x2="15" y2="15" />
            </svg>
            {t('preflight.importTitle')}
          </h3>
          <button type="button" className={styles.closeBtn} onClick={onCancel} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {/* Instructions */}
          <div className={styles.instructions}>
            <p>{t('preflight.importInstructions')}</p>
            <ol className={styles.steps}>
              <li>{t('preflight.importStep1')}</li>
              <li>{t('preflight.importStep2')}</li>
              <li>{t('preflight.importStep3')}</li>
            </ol>
            <p className={styles.columnOrder}>{t('preflight.importColumnOrder')}</p>
          </div>

          {/* Paste area */}
          <textarea
            ref={textareaRef}
            className={styles.pasteArea}
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            onPaste={handlePaste}
            placeholder={t('preflight.importPastePlaceholder')}
            rows={5}
          />

          {/* Errors */}
          {parsed && parsed.errors.length > 0 && (
            <div className={styles.errorBox}>
              {parsed.errors.map((err, i) => (
                <div key={i}>{err}</div>
              ))}
            </div>
          )}

          {/* Preview table */}
          {parsed && parsed.rows.length > 0 && (
            <>
              <div className={styles.previewHeader}>
                <span className={styles.previewCount}>
                  {t('preflight.importPreviewCount', { count: parsed.rows.length })}
                </span>
                <span className={styles.previewTotal}>
                  {t('preflight.importPreviewTotal', {
                    total: totalEstimated.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                  })}
                </span>
              </div>
              <div className={styles.tableWrap}>
                <table className={styles.previewTable}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t('preflight.resourceName')}</th>
                      <th>{t('preflight.category')}</th>
                      <th>{t('preflight.phase')}</th>
                      <th style={{ textAlign: 'end' }}>{t('preflight.targetQuantity')}</th>
                      <th>{t('preflight.unit')}</th>
                      <th style={{ textAlign: 'end' }}>{t('preflight.unitCost')}</th>
                      <th style={{ textAlign: 'end' }}>{t('preflight.estimatedTotal')}</th>
                      <th>{t('preflight.priorityLabel')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.rows.map((row, idx) => (
                      <tr key={idx} className={row.warnings.length > 0 ? styles.rowWarning : undefined}>
                        <td className={styles.rowNum}>{idx + 1}</td>
                        <td className={styles.nameCell}>
                          {row.name}
                          {row.warnings.length > 0 && (
                            <span className={styles.warningIcon} title={row.warnings.join('; ')}>⚠</span>
                          )}
                        </td>
                        <td>{t(`preflight.categories.${row.category}`)}</td>
                        <td>{t(`preflight.phases.${row.phase}`)}</td>
                        <td style={{ textAlign: 'end' }}>{row.quantity}</td>
                        <td>{row.unit}</td>
                        <td style={{ textAlign: 'end' }}>{row.unitCost.toFixed(2)}</td>
                        <td style={{ textAlign: 'end' }}>{(row.quantity * row.unitCost).toFixed(2)}</td>
                        <td>{t(`preflight.priorities.${row.priority}`)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {hasWarnings && (
                <div className={styles.warningBox}>
                  {t('preflight.importWarnings')}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button type="button" className={styles.btnSecondary} onClick={onCancel}>
            {t('preflight.cancelEdit')}
          </button>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={handleConfirm}
            disabled={!parsed || parsed.rows.length === 0 || parsed.errors.length > 0}
          >
            {t('preflight.importConfirm', { count: parsed?.rows.length ?? 0 })}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
